import { useState, useEffect, useRef, useCallback } from "react";
import Proptypes from "prop-types";
import { Button, Tabs, Tab, Box, CircularProgress } from "@mui/material";
import { DrawOutlined, ImageOutlined, BookmarkOutlined } from "@mui/icons-material";

import useDrawCanvas from "../../../hooks/useDrawCanvas";
import { FddApi } from "../../../../../ui/services/server-fdd";

const TAB_DRAW = 'draw';
const TAB_SAVED = 'saved';
const TAB_UPLOAD = 'upload';

export const ModalSign = ({elementRef, value, setValue, sign, onClear}) => {
    const containerRef = useRef(null);
    const fileInputRef = useRef(null);
    const [canvasWidth, setCanvasWidth] = useState(245);

    const [activeTab, setActiveTab] = useState(TAB_DRAW);
    const [savedSignature, setSavedSignature] = useState(null);
    const [loadingSaved, setLoadingSaved] = useState(false);
    const [savingSignature, setSavingSignature] = useState(false);
    const [isSaveSuccess, setIsSaveSuccess] = useState(false);

    const isAuthenticated = !!localStorage.getItem('authToken');

    const {handleClearCanvas} = useDrawCanvas(elementRef, value, setValue, sign);

    useEffect(() => {
        if (!containerRef.current) return;
        setCanvasWidth(containerRef.current.clientWidth);
    }, [containerRef?.current?.clientWidth]);

    const loadSavedSignature = useCallback(async () => {
        if (!isAuthenticated || savedSignature !== null) return;
        setLoadingSaved(true);
        const result = await FddApi.getSavedSignature();
        setSavedSignature((!result.hasError && result.savedSignature) ? result.savedSignature : '');
        setLoadingSaved(false);
    }, [isAuthenticated, savedSignature]);

    const handleTabChange = (_, newValue) => {
        setActiveTab(newValue);
        if (newValue === TAB_SAVED) loadSavedSignature();
        setValue('');
    };

    const handleUseSaved = () => { if (savedSignature) setValue(savedSignature); };

    const handleSaveCanvasSignature = async () => {
        if (!elementRef.current) return;
        setSavingSignature(true);
        const base64 = elementRef.current.toDataURL('image/png');
        const result = await FddApi.saveSavedSignature(base64);
        if (!result.hasError) {
            setSavedSignature(base64);
            setIsSaveSuccess(true);
            setTimeout(() => setIsSaveSuccess(false), 2500);
        }
        setSavingSignature(false);
    };

    const removeWhiteBackground = (dataUrl) => new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.naturalWidth;
            canvas.height = img.naturalHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            // Hacer transparentes los píxeles cercanos al blanco (umbral: cada canal > 220)
            for (let i = 0; i < data.length; i += 4) {
                if (data[i] > 220 && data[i + 1] > 220 && data[i + 2] > 220) {
                    data[i + 3] = 0;
                }
            }
            ctx.putImageData(imageData, 0, 0);
            resolve(canvas.toDataURL('image/png'));
        };
        img.src = dataUrl;
    });

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !file.type.startsWith('image/')) return;
        const reader = new FileReader();
        reader.onload = async (event) => {
            const processed = await removeWhiteBackground(event.target.result);
            setValue(processed);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const resolvedActiveTab = (!isAuthenticated && activeTab === TAB_SAVED) ? TAB_DRAW : activeTab;

    // Cuando ya hay firma aplicada, mostrar preview + botón Limpiar
    const isSigned = value && typeof value === 'string' && value.startsWith('data:image');

    if (isSigned) {
        return (
            <div ref={containerRef} style={{ width: '100%', textAlign: 'center' }}>
                <p style={{ fontSize: '0.8rem', color: 'var(--primary-color)', fontWeight: 600, marginBottom: '8px' }}>
                    ✓ Firma aplicada en todos los campos
                </p>
                <img
                    src={value}
                    alt="Firma aplicada"
                    style={{
                        maxWidth: '100%',
                        maxHeight: '110px',
                        border: '2px solid var(--primary-color)',
                        borderRadius: 8,
                        objectFit: 'contain',
                        background: '#fff',
                        display: 'block',
                        margin: '0 auto',
                    }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={onClear || (() => setValue(''))}
                    sx={{ mt: 1.5, fontSize: '0.78rem' }}>
                    Limpiar firma
                </Button>
            </div>
        );
    }

    return (
        <div ref={containerRef} style={{ width: '100%' }}>
            <Tabs
                value={resolvedActiveTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{ mb: 1, minHeight: 36 }}
                TabIndicatorProps={{ style: { height: 3, borderRadius: 2 } }}>
                <Tab value={TAB_DRAW} icon={<DrawOutlined fontSize="small" />} iconPosition="start" label="Dibujar" sx={{ minHeight: 36, fontSize: '0.8rem', py: 0.5 }} />
                {isAuthenticated && (
                    <Tab value={TAB_SAVED} icon={<BookmarkOutlined fontSize="small" />} iconPosition="start" label="Guardada" sx={{ minHeight: 36, fontSize: '0.8rem', py: 0.5 }} />
                )}
                <Tab value={TAB_UPLOAD} icon={<ImageOutlined fontSize="small" />} iconPosition="start" label="Subir PNG" sx={{ minHeight: 36, fontSize: '0.8rem', py: 0.5 }} />
            </Tabs>

            {resolvedActiveTab === TAB_DRAW && (
                <Box>
                    <div style={{ position: 'relative', width: `${canvasWidth}px`, height: '150px', margin: '0 auto' }}>
                        <canvas
                            ref={elementRef}
                            width={canvasWidth}
                            height={150}
                            defaultValue={value}
                            style={{
                                position: 'absolute',
                                width: `${canvasWidth}px`,
                                height: '150px',
                                border: '2px solid var(--primary-color)',
                                borderRadius: '10px',
                                backgroundColor: 'white',
                            }} />
                        <Button
                            onClick={handleClearCanvas}
                            variant="text" color="secondary" size="small"
                            sx={{ position: 'absolute', zIndex: 1, top: '5px', right: '5px', width: 'min-content', height: 'min-content' }}>
                            Limpiar
                        </Button>
                    </div>
                    {isAuthenticated && (
                        <Button size="small" variant="outlined" onClick={handleSaveCanvasSignature}
                            disabled={savingSignature}
                            sx={{ mt: 1, display: 'block', mx: 'auto', fontSize: '0.75rem' }}>
                            {savingSignature ? <CircularProgress size={12} sx={{ mr: 0.5 }} /> : null}
                            {isSaveSuccess ? '\u2713 Guardada' : 'Guardar para uso futuro'}
                        </Button>
                    )}
                </Box>
            )}

            {resolvedActiveTab === TAB_SAVED && isAuthenticated && (
                <Box sx={{ textAlign: 'center', minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {loadingSaved ? (
                        <CircularProgress size={28} />
                    ) : savedSignature ? (
                        <>
                            <img src={savedSignature} alt="Firma guardada"
                                style={{ maxWidth: '100%', maxHeight: '130px', border: '2px solid var(--primary-color)', borderRadius: 8, objectFit: 'contain', background: '#fff' }} />
                            <Button size="small" variant="contained" disableElevation onClick={handleUseSaved} sx={{ fontSize: '0.82rem' }}>
                                Usar esta firma
                            </Button>
                        </>
                    ) : (
                        <p style={{ color: '#888', fontSize: '0.85rem' }}>
                            No tienes firma guardada. Dibuja una y guarda con "Guardar para uso futuro".
                        </p>
                    )}
                </Box>
            )}

            {resolvedActiveTab === TAB_UPLOAD && (
                <Box sx={{ textAlign: 'center', minHeight: 150, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                    {value && typeof value === 'string' && value.startsWith('data:image') ? (
                        <>
                            <img src={value} alt="Firma subida"
                                style={{ maxWidth: '100%', maxHeight: '120px', border: '2px solid var(--primary-color)', borderRadius: 8, objectFit: 'contain', background: '#fff' }} />
                            <Button size="small" variant="text" color="secondary" onClick={() => { setValue(''); fileInputRef.current?.click(); }}>
                                Cambiar imagen
                            </Button>
                        </>
                    ) : (
                        <>
                            <p style={{ color: '#888', fontSize: '0.85rem', margin: 0 }}>Sube una imagen PNG/JPG de tu firma</p>
                            <Button variant="outlined" size="small" onClick={() => fileInputRef.current?.click()}>
                                Seleccionar archivo
                            </Button>
                        </>
                    )}
                    <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml"
                        onChange={handleFileUpload} style={{ display: 'none' }} />
                </Box>
            )}
        </div>
    );
};

ModalSign.propTypes = {
    elementRef: Proptypes.object,
    value: Proptypes.any,
    setValue: Proptypes.func,
    sign: Proptypes.string,
    onClear: Proptypes.func,
};